/**
 * WebinarSectionCarousel — v7 progresszív szekciónkénti webinár Carousel.
 *
 * A képernyő pontosan a MagnetPostFacebook / Carousel eredményoldal
 * mintáját követi:
 *   • PostHeader (Beállítás / Webinár tabok, vissza nyíl)
 *   • 440px-es bal panel: cím + leírás + kijelölés-workspace + chat input
 *   • jobb oldali cream kártya: felső action-pillek (Másolás / Letöltés /
 *     Mentés a Tudástárba), meta-fejléc, majd a 7 szekció-akkordion.
 *
 * Minden dia a Carousel `webinarMode` layout-ját kapja: a pill a fehér
 * kártya FÖLÖTT, jobbra ("N · PILL"), a fehér kártyán belül csak az
 * éles slide-szöveg + kis szem/másolás/details ikonok jobb alsóban.
 *
 * A finomító panel a `/conversion/webinar/callbook-funnel/refine`
 * endpointra hív; a snippet-módú válasz stringcserével kerül vissza a
 * megfelelő slide-mezőbe (első találat nyer).
 *
 * A generálás továbbra is szekciónkénti (opening → win_1 → … → qa) és
 * a háttérben fokozatosan érkezik meg minden szekció — a felhasználó
 * végig ezen a képernyőn marad, NINCS fekete köztes loading nézet.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CircleNotch, CheckCircle,
  CaretDown, CaretUp, ArrowClockwise, Eye, Copy as CopyIcon,
  DownloadSimple, PaperPlaneTilt, Paperclip, X as XIcon,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getActiveAlap } from "../lib/activeAlap";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import { PostHeader } from "./MagnetPostFacebook";

/* ────────────────────────────── konstansok ─────────────────────────── */

// A callbook_funnel (Webinárból telefonhívás) és a direct_sales
// (Közvetlen értékesítés) sub-agentek section sorrendje csak a 6.
// szekcióban tér el: call_offer (12–14 slide) helyett direct_offer
// (18–24 slide, Közvetlen ajánlati rész). Az első 5 szekció és a
// záró qa szekció bit-szinten változatlan.
const SECTION_ORDER_CALLBOOK = ["opening", "win_1", "win_2", "win_3", "reanchor", "call_offer", "qa"];
const SECTION_ORDER_DIRECT   = ["opening", "win_1", "win_2", "win_3", "reanchor", "direct_offer", "qa"];
const SECTION_META = {
  opening:      { name: "Lehetőségkeretezés és nyitás", range: "8–12 slide" },
  win_1:        { name: "Győzelem 1",                     range: "5–7 slide" },
  win_2:        { name: "Győzelem 2",                     range: "5–7 slide" },
  win_3:        { name: "Győzelem 3",                     range: "5–7 slide" },
  reanchor:     { name: "Újrahorgonyzás és átvezetés",    range: "3–4 slide" },
  call_offer:   { name: "Telefonhívás ajánlati rész",     range: "12–14 slide" },
  direct_offer: { name: "Közvetlen ajánlati rész",        range: "34–48 slide" },
  qa:           { name: "Kérdések és utolsó CTA",         range: "4 slide" },
};

// A direct_sales variant qa szekció NAGYOBB (6-9 slide, GYIK + záró
// CTA + köszönés struktúrával), és MÁS a neve. A callbook_funnel qa
// szekció változatlan (4 slide, callback FAQ). A `getSectionMeta`
// helper visszaadja a variant-tudatos SECTION_META objektumot.
const SECTION_META_DIRECT_QA_OVERRIDE = {
  qa: { name: "GYIK és záró CTA", range: "6–9 slide" },
};

function getSectionMeta(agentVariant) {
  if (agentVariant === "direct_sales") {
    return { ...SECTION_META, ...SECTION_META_DIRECT_QA_OVERRIDE };
  }
  return SECTION_META;
}

// Webinár layer accent — arany. A rendszer elsődleges akcent-színe (lila)
// a chat input küldés-gombjában és a quick-refine chipeken jelenik meg.
const GOLD = "#d4a800";
const GOLD_BG = "rgba(255,219,92,0.14)";
const GOLD_BORDER = "rgba(255,219,92,0.36)";

// Ugyanaz a 6-elemű quick-refine készlet, mint a Poszt / Carousel-ben.
const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb",     instruction: "Erősebb érzelmi feszültség, mélyebb fájdalom, konkrétabb jelenet, nagyobb hatás." },
  { id: "soft",     label: "Lágyabb",     instruction: "Melegebb, empatikusabb, barátságosabb, több megértés." },
  { id: "concrete", label: "Konkrétabb",  instruction: "Több konkrét részlet, időpont, hely, tárgy, érzés — semmi általánosítás." },
  { id: "story",    label: "Több sztori", instruction: "Cseréld ki vagy bővítsd ki egy rövid, életszagú személyes vagy hipotetikus jelenettel." },
  { id: "humor",    label: "Humorosabb",  instruction: "Adj bele finom öniróniát vagy könnyed humort, de maradj empatikus." },
  { id: "surprise", label: "Lepj meg!",   instruction: "Legyél kreatívabb, váratlanabb, erősebb hook vagy fordulat ebben a részben." },
];

/* ────────────────────────────── helpers ────────────────────────────── */

function initialSections(initialOpening, sectionOrder, savedCarouselContent) {
  // 1) Mentett Tudástár-anyag: minden szekciót előre kitöltünk a
  // mentett slide-okkal, minden szekció `ready` állapotban. Az első
  // szekció (opening) nyitva, a többi becsukva a jobb áttekinthetőségért.
  if (savedCarouselContent) {
    const diak = Array.isArray(savedCarouselContent)
      ? savedCarouselContent
      : Array.isArray(savedCarouselContent?.diak)
        ? savedCarouselContent.diak
        : [];
    const grouped = {};
    for (const slide of diak) {
      const sid = slide?.section_id;
      if (!sid || !sectionOrder.includes(sid)) continue;
      if (!grouped[sid]) grouped[sid] = [];
      // A saved slide már a Tudástár shape-jében van (dia_szam, pill,
      // visible_headline, visible_bullets, visible_question, visible_cta,
      // visual_suggestion, speaker_notes). A Carousel `result.slides` a
      // WRITER shape-et várja (`slide_number` a `dia_szam`, minden más
      // ugyanaz). Egy egyszerű remap eleget tesz mindkettőnek.
      grouped[sid].push({
        slide_number: slide.dia_szam || grouped[sid].length + 1,
        pill: slide.pill || "",
        visible_headline: slide.visible_headline || slide.cim || "",
        visible_bullets: Array.isArray(slide.visible_bullets) ? slide.visible_bullets : [],
        visible_question: slide.visible_question || "",
        visible_cta: slide.visible_cta || "",
        visual_suggestion: slide.visual_suggestion || "",
        speaker_notes: slide.speaker_notes || "",
      });
    }
    const obj = {};
    for (const sid of sectionOrder) {
      const slides = grouped[sid] || [];
      obj[sid] = {
        status: slides.length ? "ready" : "waiting",
        jobId: null,
        result: slides.length ? { slides, section_summary: "" } : null,
        error: null,
        expanded: sid === "opening" && slides.length > 0,
      };
    }
    return obj;
  }

  // 2) Éles generálás (default): opening = generating, többi = waiting.
  const obj = {};
  for (const sid of sectionOrder) {
    if (sid === "opening" && initialOpening) {
      obj[sid] = { status: "ready", jobId: null, result: initialOpening, error: null, expanded: true };
    } else {
      obj[sid] = {
        status: sid === "opening" ? "generating" : "waiting",
        jobId: null, result: null, error: null,
        // Az első nem-opening szekció automatikusan nyíljon meg, amint kész.
        expanded: false,
      };
    }
  }
  return obj;
}

// Egy slide látható szövegét összeállítja (a becsukott másolás-ikon
// ezt teszi a vágólapra).
function slideVisibleText(slide) {
  const parts = [];
  if (slide.visible_headline) parts.push(slide.visible_headline);
  if (Array.isArray(slide.visible_bullets) && slide.visible_bullets.length) {
    for (const b of slide.visible_bullets) parts.push("• " + b);
  }
  if (slide.visible_question) parts.push(slide.visible_question);
  if (slide.visible_cta) parts.push(slide.visible_cta);
  return parts.join("\n").trim();
}

// Egy slide teljes szövege (nyitott másolás → látható + vizuális + jegyzet).
function slideFullText(slide) {
  const visible = slideVisibleText(slide);
  const extra = [];
  if (slide.visual_suggestion) extra.push("Vizuális javaslat:\n" + slide.visual_suggestion);
  if (slide.speaker_notes)     extra.push("Előadói jegyzet:\n" + slide.speaker_notes);
  return [visible, ...extra].filter(Boolean).join("\n\n");
}

// A teljes webinár szöveges reprezentációja — a finomító endpoint,
// másolás és letöltés is ezt használja.
function buildFullWebinarText(sections, sectionOrder, sectionMeta) {
  const chunks = [];
  for (const sid of sectionOrder) {
    const sec = sections[sid];
    if (sec?.status !== "ready" || !sec.result?.slides?.length) continue;
    chunks.push(`### ${sectionMeta[sid].name.toUpperCase()} ###`);
    for (const slide of sec.result.slides) {
      const pill = slide.pill ? ` · ${slide.pill}` : "";
      chunks.push(`— SLIDE ${slide.slide_number}${pill} —`);
      chunks.push(slideFullText(slide));
    }
    chunks.push("");
  }
  return chunks.join("\n").trim();
}

// A mentéshez a Carousel-kompatibilis `carousel_content` alakot építjük fel.
function buildCarouselContent(sections, inputs, sectionOrder, sectionMeta) {
  const diak = [];
  let running = 1;
  for (const sid of sectionOrder) {
    const sec = sections[sid];
    if (sec?.status !== "ready" || !sec.result?.slides?.length) continue;
    for (const slide of sec.result.slides) {
      diak.push({
        dia_szam: running,
        kategoria: slide.pill || sectionMeta[sid].name,
        pill: slide.pill || sectionMeta[sid].name,
        cim: slide.visible_headline || "",
        visible_headline: slide.visible_headline || "",
        visible_bullets: Array.isArray(slide.visible_bullets) ? slide.visible_bullets : [],
        visible_question: slide.visible_question || "",
        visible_cta: slide.visible_cta || "",
        szoveg: slideVisibleText(slide),
        visual_suggestion: slide.visual_suggestion || "",
        speaker_notes: slide.speaker_notes || "",
        section_id: sid,
        section_name: sectionMeta[sid].name,
      });
      running += 1;
    }
  }
  return {
    diak,
    leiras: "",
    miert_mukodik: "",
    alternativ_horgok: [],
    webinar_cim: inputs?.webinar_cim || "",
  };
}

// Szöveg normalizálás a robust snippet-match-hez. A DOM-ból származó
// kijelölés gyakran különbözik a source string-től:
// - whitespace collapse (több szóköz → 1)
// - sortörések (<br>, <p>, list-item boundary → \n vagy szóköz)
// - non-breaking space (\u00A0)
// - smart quotes vs. straight quotes
// - unicode dash variants (– — - vs. hyphen)
// - bullet karakter előtag (• 1. stb.)
// Így a match sokkal engedékenyebb, mégsem téves.
function _normalizeForMatch(s) {
  if (typeof s !== "string") return "";
  return s
    // Bullet előtagok eltávolítása (a DOM ::before pseudo NEM kerül a
    // selection-be, de biztosítékként lekezeljük, ha valahol prependelve
    // van a text-node-hoz).
    .replace(/^[\s]*[•·●○◦]+[\s]*/g, "")
    // Smart quotes → straight
    .replace(/[\u2018\u2019\u201A\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201E\u2033]/g, '"')
    // En-dash / em-dash → hyphen
    .replace(/[\u2013\u2014]/g, "-")
    // Non-breaking space → space
    .replace(/\u00A0/g, " ")
    // Több whitespace (spaces, tabs, newlines) → 1 space
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

// Fuzzy substring match: keresi a normalizált quote-ot a normalizált
// haystack-ben; ha megtalálja, visszaadja az EREDETI (nem normalizált)
// haystack-en a match tartományát a source-map bejegyzés alapján.
function _findFuzzyRange(haystack, needle) {
  if (typeof haystack !== "string" || typeof needle !== "string") return null;
  if (!needle) return null;
  // Gyors út: pontos match
  const exactIdx = haystack.indexOf(needle);
  if (exactIdx >= 0) return { start: exactIdx, end: exactIdx + needle.length };
  // Normalizált match — a normalizált haystack index-ét visszatérképezzük
  // az eredeti haystack-re karakterről karakterre.
  const normHay = _normalizeForMatch(haystack);
  const normNeedle = _normalizeForMatch(needle);
  if (!normHay || !normNeedle) return null;
  const nIdx = normHay.indexOf(normNeedle);
  if (nIdx < 0) return null;
  // Rekonstruáljuk a source-map-et: minden eredeti char pozícióhoz a
  // normalizált string-ben ő milyen index (vagy -1 ha eldobódott).
  const sourceMap = [];
  let normPos = 0;
  let prevWasSpace = false;
  let startedTrim = false;
  for (let i = 0; i < haystack.length; i++) {
    const ch = haystack[i];
    const norm = _normalizeForMatch(ch);
    // Whitespace: csak akkor számít, ha nem folytatólagos
    if (/\s/.test(ch) || ch === "\u00A0") {
      if (startedTrim && !prevWasSpace) {
        sourceMap.push(normPos);
        normPos += 1;
        prevWasSpace = true;
      } else {
        sourceMap.push(-1);
      }
      continue;
    }
    if (!norm) { sourceMap.push(-1); continue; }
    startedTrim = true;
    prevWasSpace = false;
    // A normalizált karakter hozzáadódik — a `norm.length` mindig 1 itt
    // a fenti karakter-cserék miatt, de biztonságból lekezeljük.
    sourceMap.push(normPos);
    normPos += norm.length;
  }
  // Keressük meg az eredeti string-ben azt a start / end pozíciót, ami
  // a normalizált nIdx / nIdx+normNeedle.length-nek felel meg.
  let origStart = -1, origEnd = -1;
  for (let i = 0; i < sourceMap.length; i++) {
    if (origStart < 0 && sourceMap[i] === nIdx) origStart = i;
    if (sourceMap[i] === nIdx + normNeedle.length - 1) { origEnd = i + 1; }
  }
  if (origStart < 0 || origEnd < 0) return null;
  return { start: origStart, end: origEnd };
}

// Robust snippet-csere `ctx` alapján. Fallback ladder:
//  1) pontos slide + pontos mező + pontos szövegcsere
//  2) pontos slide + pontos mező + whitespace/unicode-normalizált csere
//  3) bullet_index-alapú csere (ha a kijelölés egy bullet volt)
//  4) több bullet kijelölése esetén bullet_start_index .. bullet_end_index közötti bullet-tartomány cseréje
//  5) fuzzy match ugyanazon a slide-on belül minden mezőben
//  6) fuzzy match minden slide-ban (utolsó lehetőség)
function applySnippetReplacement(sections, quote, snippet, sectionOrder, ctx) {
  const q = (quote || "").trim();
  if (!q) return { sections, changed: false };
  const next = { ...sections };

  // A ctx-be foglalt slide struktúrája: { sectionId, slideNumber,
  // field, bulletIndex, bulletStartIndex, bulletEndIndex }.
  const targetSectionId = ctx?.sectionId || null;
  const targetSlideNumber = ctx?.slideNumber || null;
  const targetField = ctx?.field || null;
  const bulletIndex = Number.isInteger(ctx?.bulletIndex) ? ctx.bulletIndex : null;
  const bulletStart = Number.isInteger(ctx?.bulletStartIndex) ? ctx.bulletStartIndex : null;
  const bulletEnd = Number.isInteger(ctx?.bulletEndIndex) ? ctx.bulletEndIndex : null;

  const cloneSection = (sid) => {
    const sec = next[sid];
    if (!sec?.result?.slides) return null;
    const newSlides = sec.result.slides.map((s) => ({ ...s }));
    return { sid, sec, newSlides };
  };

  const commit = (sid, newSlides) => {
    next[sid] = { ...next[sid], result: { ...next[sid].result, slides: newSlides } };
  };

  const trySlide = (slide, allowField, strategy) => {
    // strategy: 'exact' | 'fuzzy'
    const fields = allowField
      ? [allowField]
      : ["visible_headline", "visible_question", "visible_cta", "visual_suggestion", "speaker_notes"];
    for (const key of fields) {
      const v = slide[key];
      if (typeof v !== "string" || !v) continue;
      if (strategy === "exact") {
        const idx = v.indexOf(q);
        if (idx >= 0) {
          slide[key] = v.slice(0, idx) + snippet + v.slice(idx + q.length);
          return true;
        }
      } else if (strategy === "fuzzy") {
        const r = _findFuzzyRange(v, q);
        if (r) {
          slide[key] = v.slice(0, r.start) + snippet + v.slice(r.end);
          return true;
        }
      }
    }
    // bullets
    if (Array.isArray(slide.visible_bullets)) {
      if (bulletIndex !== null && bulletIndex >= 0 && bulletIndex < slide.visible_bullets.length) {
        // Direct bullet by index
        const bv = slide.visible_bullets[bulletIndex];
        if (typeof bv === "string") {
          if (strategy === "exact" && bv.indexOf(q) >= 0) {
            const nb = [...slide.visible_bullets];
            nb[bulletIndex] = bv.replace(q, snippet);
            slide.visible_bullets = nb;
            return true;
          }
          if (strategy === "fuzzy") {
            const r = _findFuzzyRange(bv, q);
            if (r) {
              const nb = [...slide.visible_bullets];
              nb[bulletIndex] = bv.slice(0, r.start) + snippet + bv.slice(r.end);
              slide.visible_bullets = nb;
              return true;
            }
            // Whole-bullet replace: ha a kijelölt szöveg a teljes
            // bullet volt (esetleg trim-mel), cseréljük az egész
            // bulletet a snippettel.
            if (_normalizeForMatch(bv) === _normalizeForMatch(q)) {
              const nb = [...slide.visible_bullets];
              nb[bulletIndex] = snippet;
              slide.visible_bullets = nb;
              return true;
            }
          }
        }
      }
      // Multi-bullet range replace: ha a kijelölés több bulletöt
      // érint (bulletStart..bulletEnd), cseréljük a tartományt a
      // snippet sortöréseivel felbontva.
      if (bulletStart !== null && bulletEnd !== null && bulletEnd > bulletStart) {
        const bs = Math.max(0, bulletStart);
        const be = Math.min(slide.visible_bullets.length, bulletEnd + 1);
        const newBullets = snippet.split(/\r?\n+/).map((s) => s.replace(/^[\s]*[•·●○◦-]+[\s]*/, "").trim()).filter((s) => s.length);
        if (newBullets.length) {
          slide.visible_bullets = [
            ...slide.visible_bullets.slice(0, bs),
            ...newBullets,
            ...slide.visible_bullets.slice(be),
          ];
          return true;
        }
      }
      // Fallback: normál iteráció bullets-en
      for (let i = 0; i < slide.visible_bullets.length; i++) {
        const bv = slide.visible_bullets[i];
        if (typeof bv !== "string") continue;
        if (strategy === "exact" && bv.indexOf(q) >= 0) {
          const nb = [...slide.visible_bullets];
          nb[i] = bv.replace(q, snippet);
          slide.visible_bullets = nb;
          return true;
        }
        if (strategy === "fuzzy") {
          const r = _findFuzzyRange(bv, q);
          if (r) {
            const nb = [...slide.visible_bullets];
            nb[i] = bv.slice(0, r.start) + snippet + bv.slice(r.end);
            slide.visible_bullets = nb;
            return true;
          }
        }
      }
    }
    return false;
  };

  // Célzott csere: ha van ctx-ünk, először pontosan azon a slide-on
  // dolgozzunk (exact, majd fuzzy).
  if (targetSectionId && targetSlideNumber) {
    const cs = cloneSection(targetSectionId);
    if (cs) {
      const idx = cs.newSlides.findIndex((s) => (s.slide_number || 0) === Number(targetSlideNumber));
      if (idx >= 0) {
        // strategy 1: exact match a target field-en
        if (trySlide(cs.newSlides[idx], targetField, "exact")) {
          commit(cs.sid, cs.newSlides);
          return { sections: next, changed: true };
        }
        // strategy 2: fuzzy match a target field-en
        if (trySlide(cs.newSlides[idx], targetField, "fuzzy")) {
          commit(cs.sid, cs.newSlides);
          return { sections: next, changed: true };
        }
        // strategy 3: fuzzy match minden mezőben ugyanazon a slide-on
        if (trySlide(cs.newSlides[idx], null, "fuzzy")) {
          commit(cs.sid, cs.newSlides);
          return { sections: next, changed: true };
        }
      }
    }
  }

  // Ha nincs ctx (vagy nem sikerült a célzott csere), fuzzy match minden
  // slide-ban. Elsőnek talált slide + mező nyer.
  for (const sid of sectionOrder) {
    const cs = cloneSection(sid);
    if (!cs) continue;
    for (let i = 0; i < cs.newSlides.length; i++) {
      if (trySlide(cs.newSlides[i], null, "exact") || trySlide(cs.newSlides[i], null, "fuzzy")) {
        commit(cs.sid, cs.newSlides);
        return { sections: next, changed: true };
      }
    }
  }

  return { sections: next, changed: false };
}

// Snippet cseréje slide-mezőkben. Első találat nyer. Backward-compat
// wrapper (ctx nélkül) az `applySnippetReplacement` fuzzy fallback-jét
// használja.
function replaceSnippetInSections(sections, quote, snippet, sectionOrder) {
  return applySnippetReplacement(sections, quote, snippet, sectionOrder, null);
}

/* ─────────────────────────── al-komponensek ────────────────────────── */

function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled }) {
  const bg = active ? "rgba(29,158,117,0.10)" : GOLD_BG;
  const border = active ? "rgba(29,158,117,0.30)" : GOLD_BORDER;
  const color = active ? "#1d9e75" : "#8a6d00";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: bg, border: `1px solid ${border}`,
        borderRadius: 999, color,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

/**
 * SlideCard — Carousel-mintájú fehér slide-kártya.
 *
 * A pill (`N · KATEGÓRIA`) a fehér kártya FÖLÖTT, jobbra jelenik meg —
 * a fehér kártyán belül CSAK az éles slide-szöveg. A jobb alsóban két
 * kis ikon: szem (details toggle: Vizuális javaslat + Előadói jegyzet)
 * és másolás (a slide szövegét másolja — nyitva a details is benne).
 */
function SlideCard({ slide, sectionIdx, sectionId }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const num = slide.slide_number || sectionIdx + 1;
  const pill = (slide.pill || "").trim();
  const bullets = Array.isArray(slide.visible_bullets) ? slide.visible_bullets : [];
  const visual = (slide.visual_suggestion || "").trim();
  const speaker = (slide.speaker_notes || "").trim();
  const hasDetails = !!(visual || speaker);

  // Context attribútumok a slide gyökér elemre — a szöveg-kijelölés
  // handler a legközelebbi ilyen ősre kúszik fel, és a `data-webinar-*`
  // attribútumokból pontosan tudja, melyik szekció / slide / mező /
  // bullet-index van kijelölve. Így az „Alkalmazom" gomb SOSEM
  // találgat, hanem a pontos struktúra alapján cserél.
  const slideCtx = {
    "data-webinar-slide-root": "1",
    "data-webinar-section-id": sectionId || "",
    "data-webinar-slide-number": String(num),
  };

  const handleCopy = async (e) => {
    e?.stopPropagation();
    const text = open ? slideFullText(slide) : slideVisibleText(slide);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Másolás nem sikerült.");
    }
  };

  return (
    <div style={{ marginBottom: 20 }}>
      {/* PILL — kártyán KÍVÜL, jobbra: "N · PILL" */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <span
          data-testid={`webinar-section-slide-${num}-pill`}
          style={{
            fontSize: 13, color: GOLD, fontWeight: 600,
            letterSpacing: "0.08em", textTransform: "uppercase",
            background: GOLD_BG, border: `1px solid ${GOLD_BORDER}`,
            padding: "6px 14px", borderRadius: 999,
          }}
        >
          {num}{pill ? ` · ${pill}` : ""}
        </span>
      </div>

      {/* KÁRTYA */}
      <div
        data-testid={`webinar-section-slide-${num}`}
        {...slideCtx}
        style={{
          position: "relative",
          padding: "22px 26px 30px",
          borderRadius: 12,
          background: "#ffffff",
          border: "0.5px solid rgba(20, 30, 40, 0.08)",
        }}
      >
        {slide.visible_headline && (
          <div
            className="font-heading"
            data-webinar-field="visible_headline"
            style={{
              fontSize: sectionIdx === 0 ? 21 : 18,
              fontWeight: 700, color: "#0a0a12",
              lineHeight: 1.3, letterSpacing: "-0.012em",
              marginBottom: (bullets.length || slide.visible_question || slide.visible_cta) ? 14 : 0,
              paddingRight: 12,
            }}
          >
            {slide.visible_headline}
          </div>
        )}

        {bullets.length > 0 && (
          <ul
            data-webinar-slide-body
            data-webinar-field="visible_bullets"
            style={{
              listStyle: "none", padding: 0, margin: 0,
              display: "flex", flexDirection: "column", gap: 6,
            }}
          >
            {bullets.map((b, i) => (
              <li
                key={i}
                data-webinar-field="visible_bullets"
                data-webinar-bullet-index={i}
                style={{
                  fontSize: 13.5, color: "#3a4654", fontWeight: 400,
                  lineHeight: 1.6, paddingLeft: 18, position: "relative",
                }}
              >
                <span style={{
                  position: "absolute", left: 0, top: 6,
                  width: 5, height: 5, borderRadius: "50%",
                  background: GOLD,
                }} />
                {b}
              </li>
            ))}
          </ul>
        )}

        {slide.visible_question && (
          <div
            data-webinar-field="visible_question"
            style={{
              marginTop: bullets.length ? 12 : 0,
              fontSize: 14, color: GOLD, fontWeight: 500,
              lineHeight: 1.55, fontStyle: "italic",
            }}
          >
            {slide.visible_question}
          </div>
        )}

        {slide.visible_cta && (
          <div
            data-webinar-field="visible_cta"
            style={{
              marginTop: bullets.length || slide.visible_question ? 14 : 6,
              display: "inline-flex", alignItems: "center",
              background: GOLD, color: "#fff",
              fontSize: 13, fontWeight: 600,
              padding: "8px 16px", borderRadius: 999,
            }}
          >
            {slide.visible_cta}
          </div>
        )}

        {/* Ikonsor a jobb alsó sarokban: szem (details) + másolás */}
        <div style={{
          position: "absolute", right: 12, bottom: 10,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <button
            type="button"
            onClick={handleCopy}
            data-testid={`webinar-section-slide-${num}-copy`}
            aria-label={copied ? "Másolva" : "Slide szövegének másolása"}
            style={{
              background: "transparent", border: "none",
              cursor: "pointer", padding: 4, borderRadius: 6,
              color: copied ? GOLD : "#9aa4ad",
              opacity: copied ? 1 : 0.5,
              display: "inline-flex", alignItems: "center",
              transition: "color 0.15s, opacity 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = copied ? "1" : "0.5"; }}
          >
            {copied
              ? <CheckCircle size={15} weight="regular" />
              : <CopyIcon size={15} weight="regular" />
            }
          </button>
          {hasDetails && (
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              data-testid={`webinar-section-slide-${num}-details`}
              aria-label={open ? "Részletek bezárása" : "Részletek megnyitása"}
              style={{
                background: "transparent", border: "none",
                cursor: "pointer", padding: 4, borderRadius: 6,
                color: open ? GOLD : "#9aa4ad",
                opacity: open ? 1 : 0.5,
                display: "inline-flex", alignItems: "center",
                transition: "color 0.15s, opacity 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = open ? "1" : "0.5"; }}
            >
              <ImageIcon size={15} weight={open ? "fill" : "regular"} />
            </button>
          )}
        </div>

        {/* Részletek panel */}
        {open && hasDetails && (
          <div
            data-testid={`webinar-section-slide-${num}-details-body`}
            data-webinar-details-body
            style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 12 }}
          >
            {visual && (
              <div style={{
                padding: "12px 14px",
                background: "rgba(20, 30, 40, 0.035)",
                border: "0.5px solid rgba(20, 30, 40, 0.06)",
                borderRadius: 8,
                fontSize: 12.5, color: "#3a4654", lineHeight: 1.65,
              }}>
                <div style={{
                  color: GOLD, fontWeight: 700,
                  fontSize: 10, letterSpacing: "0.12em",
                  textTransform: "uppercase", marginBottom: 6,
                }}>
                  Vizuális javaslat
                </div>
                <div data-webinar-field="visual_suggestion" style={{ whiteSpace: "pre-wrap" }}>{visual}</div>
              </div>
            )}
            {speaker && (
              <div style={{
                padding: "12px 14px",
                background: "rgba(20, 30, 40, 0.035)",
                border: "0.5px solid rgba(20, 30, 40, 0.06)",
                borderRadius: 8,
                fontSize: 12.5, color: "#3a4654", lineHeight: 1.7,
              }}>
                <div style={{
                  color: GOLD, fontWeight: 700,
                  fontSize: 10, letterSpacing: "0.12em",
                  textTransform: "uppercase", marginBottom: 6,
                }}>
                  Előadói jegyzet
                </div>
                <div data-webinar-field="speaker_notes" style={{ whiteSpace: "pre-wrap" }}>{speaker}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── fő komponens ─────────────────────────── */

export default function WebinarSectionCarousel({ inputs, vazlat, vazlatRaw, initialOpening = null, onClose, agentVariant = "callbook_funnel", onSavedToVault = null, savedCarouselContent = null, readOnly = false, apiEndpoints = null }) {
  // Endpoint override — a Landing Varázsló azonos flow-ját identikusan
  // futtatja, DE saját backend endpointokra. Default: webinár URL-ek
  // (bit-szinten változatlanok). A Landing komponens `apiEndpoints`
  // propon keresztül override-olja őket saját `/landing-registration/*`
  // endpointjaira. Ha nincs override, a webinár default fut.
  const EP = apiEndpoints || {
    sectionGenerateAsync: "/conversion/webinar/section/generate-async",
    sectionJob: (jobId) => `/conversion/webinar/section/job/${jobId}`,
    refine: "/conversion/webinar/callbook-funnel/refine",
    save: "/conversion/webinar/callbook-funnel/save",
  };
  // Variant-tudatos section sorrend: a callbook_funnel a call_offer,
  // a direct_sales a direct_offer 6. szekciót használja. A helyi
  // helper funkciók (buildFullWebinarText / buildCarouselContent /
  // replaceSnippetInSections / initialSections) ezt a sectionOrder-t
  // kapják paraméterként, így csak egy helyen dől el, hogy melyik
  // sub-agent futásáról van szó. A `sectionMeta` a variant-tudatos
  // meta lookup (qa név / slide range direct_sales-nél más).
  const sectionOrder = agentVariant === "direct_sales"
    ? SECTION_ORDER_DIRECT
    : SECTION_ORDER_CALLBOOK;
  const sectionMeta = getSectionMeta(agentVariant);
  const [sections, setSections] = useState(() => initialSections(initialOpening, sectionOrder, savedCarouselContent));
  const startedRef = useRef(false);
  const activeAlap = getActiveAlap();
  const previewRef = useRef(null);

  // Finomítás állapota — Carousel / poszt eredményoldal mintájára.
  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle | loading | suggestion | applied
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);

  // Felső action pillek állapota.
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingVault, setSavingVault] = useState(false);

  const setSection = useCallback((sid, patch) => {
    setSections((prev) => ({ ...prev, [sid]: { ...prev[sid], ...patch } }));
  }, []);

  /* ─── progresszív szekció-generálás ─────────────────────────────── */

  const buildBody = useCallback((sectionId, previousSummaries) => ({
    voice_id: activeAlap.voice || null,
    client_id: activeAlap.client || null,
    offer_id: activeAlap.offer || null,
    section_id: sectionId,
    agent_variant: agentVariant,
    webinar_cim: inputs?.webinar_cim || "",
    mit_tanuljon: inputs?.mit_tanuljon || "",
    miert_te: inputs?.miert_te || "",
    hova_hivjuk: inputs?.hova_hivjuk || "",
    vazlat_raw: vazlatRaw || "",
    vazlat: vazlat || null,
    previous_summaries: previousSummaries,
  }), [activeAlap, inputs, vazlat, vazlatRaw, agentVariant]);

  const pollJob = useCallback(async (sectionId, jobId) => {
    for (let attempt = 0; attempt < 60; attempt++) {
      await new Promise((r) => setTimeout(r, 3500));
      try {
        const res = await api.get(EP.sectionJob(jobId));
        const data = res.data || {};
        if (data.status === "done" && data.result) {
          // Ha ez az első, ami "ready", legyen automatikusan nyitva.
          setSections((prev) => {
            const anyOpen = sectionOrder.some(
              (s) => prev[s]?.status === "ready" && prev[s]?.expanded
            );
            return {
              ...prev,
              [sectionId]: {
                ...prev[sectionId],
                status: "ready", result: data.result, error: null,
                expanded: sectionId === "opening" ? true : !anyOpen ? true : false,
              },
            };
          });
          return data.result;
        }
        if (data.status === "error") {
          setSection(sectionId, { status: "failed", error: data.error || "Generálás sikertelen." });
          return null;
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || "Státusz lekérdezés sikertelen.";
        setSection(sectionId, { status: "failed", error: msg });
        return null;
      }
    }
    setSection(sectionId, { status: "failed", error: "Időtúllépés — a szekció generálása túl sokáig tart." });
    return null;
  }, [setSection]);

  const generateSection = useCallback(async (sectionId, previousSummaries = []) => {
    setSection(sectionId, { status: "generating", error: null });
    try {
      const res = await api.post(
        EP.sectionGenerateAsync,
        buildBody(sectionId, previousSummaries),
      );
      const jobId = res.data?.job_id;
      if (!jobId) {
        setSection(sectionId, { status: "failed", error: "Nem sikerült elindítani a generálást." });
        return null;
      }
      setSection(sectionId, { jobId });
      return await pollJob(sectionId, jobId);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Generálás indítása sikertelen.";
      setSection(sectionId, { status: "failed", error: msg });
      return null;
    }
  }, [buildBody, pollJob, setSection]);

  const runProgressive = useCallback(async () => {
    const summaries = [];
    if (initialOpening) {
      summaries.push({
        section_id: "opening",
        section_name: sectionMeta["opening"].name,
        section_summary: initialOpening.section_summary || "",
      });
    }
    for (const sid of sectionOrder) {
      if (sid === "opening" && initialOpening) continue;
      const result = await generateSection(sid, [...summaries]);
      if (result) {
        summaries.push({
          section_id: sid,
          section_name: sectionMeta[sid].name,
          section_summary: result.section_summary || "",
        });
      } else {
        break;
      }
    }
  }, [generateSection, initialOpening, sectionOrder, sectionMeta]);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // Read-only / saved viewer mode: minden szekció már a `savedCarouselContent`
    // alapján ki lett töltve az `initialSections`-ben — NEM indítunk generálást.
    if (readOnly || savedCarouselContent) return;
    runProgressive();
  }, [runProgressive, readOnly, savedCarouselContent]);

  const retrySection = async (sid) => {
    const prevSummaries = [];
    for (const earlier of sectionOrder) {
      if (earlier === sid) break;
      const sec = sections[earlier];
      if (sec?.status === "ready" && sec.result) {
        prevSummaries.push({
          section_id: earlier,
          section_name: sectionMeta[earlier].name,
          section_summary: sec.result.section_summary || "",
        });
      }
    }
    const result = await generateSection(sid, prevSummaries);
    if (result) {
      const remaining = sectionOrder.slice(sectionOrder.indexOf(sid) + 1);
      const summariesAfter = [...prevSummaries, {
        section_id: sid,
        section_name: sectionMeta[sid].name,
        section_summary: result.section_summary || "",
      }];
      for (const next of remaining) {
        const cur = sections[next];
        if (cur?.status !== "waiting") {
          if (cur?.result?.section_summary) summariesAfter.push({
            section_id: next,
            section_name: sectionMeta[next].name,
            section_summary: cur.result.section_summary || "",
          });
          continue;
        }
        const r = await generateSection(next, [...summariesAfter]);
        if (r) summariesAfter.push({
          section_id: next,
          section_name: sectionMeta[next].name,
          section_summary: r.section_summary || "",
        });
        else break;
      }
    }
  };

  const toggleExpand = (sid) => {
    setSection(sid, { expanded: !sections[sid].expanded });
  };

  /* ─── szöveg-kijelölés (browser mouseup) ────────────────────────── */

  // A kijelöléshez tartozó strukturális kontextust külön state-ben
  // tartjuk (section_id / slide_number / field / bullet_index /
  // bullet_start_index / bullet_end_index). Ez ÁTUTAZIK a suggestion
  // objektumba is (a refine kimeneti oldalára), így az „Alkalmazom"
  // gomb pontosan azt a slide-mezőt cseréli le, ahonnan a kijelölés
  // eredetileg jött — SOSEM találgat.
  const [selectionCtx, setSelectionCtx] = useState(null);

  useEffect(() => {
    const readFieldCtx = (node) => {
      let field = null;
      let bulletIndex = null;
      // Legközelebbi ős, amin [data-webinar-field] attribútum van.
      let cur = node && node.nodeType === 3 ? node.parentNode : node;
      while (cur && cur !== document.body) {
        if (cur.dataset && cur.dataset.webinarField) {
          field = cur.dataset.webinarField;
          if (cur.dataset.webinarBulletIndex !== undefined) {
            const bi = parseInt(cur.dataset.webinarBulletIndex, 10);
            if (!Number.isNaN(bi)) bulletIndex = bi;
          }
          break;
        }
        cur = cur.parentNode;
      }
      // Slide + section ID a slide root-ról.
      let sectionId = null;
      let slideNumber = null;
      let sroot = cur;
      while (sroot && sroot !== document.body) {
        if (sroot.dataset && sroot.dataset.webinarSlideRoot) {
          sectionId = sroot.dataset.webinarSectionId || null;
          const sn = parseInt(sroot.dataset.webinarSlideNumber || "", 10);
          if (!Number.isNaN(sn)) slideNumber = sn;
          break;
        }
        sroot = sroot.parentNode;
      }
      return { field, bulletIndex, sectionId, slideNumber };
    };

    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      // Anchor + focus context: ha eltér a bullet-index, akkor több
      // bulletöt érint a kijelölés.
      const anchorCtx = readFieldCtx(sel.anchorNode);
      const focusCtx = readFieldCtx(sel.focusNode);
      const ctx = {
        text: text.slice(0, 600),
        sectionId: anchorCtx.sectionId,
        slideNumber: anchorCtx.slideNumber,
        field: anchorCtx.field,
        bulletIndex: anchorCtx.bulletIndex,
        bulletStartIndex: null,
        bulletEndIndex: null,
      };
      // Ha a kijelölés több bullet-en át fut, számoljuk ki a start/end
      // index-et. Az anchor és focus sorrendje függhet attól, hogy fel
      // vagy le húzta a user a szelekciót — a matematikai min/max
      // biztonságos.
      if (
        anchorCtx.field === "visible_bullets" &&
        focusCtx.field === "visible_bullets" &&
        anchorCtx.sectionId === focusCtx.sectionId &&
        anchorCtx.slideNumber === focusCtx.slideNumber &&
        Number.isInteger(anchorCtx.bulletIndex) &&
        Number.isInteger(focusCtx.bulletIndex) &&
        anchorCtx.bulletIndex !== focusCtx.bulletIndex
      ) {
        ctx.bulletStartIndex = Math.min(anchorCtx.bulletIndex, focusCtx.bulletIndex);
        ctx.bulletEndIndex = Math.max(anchorCtx.bulletIndex, focusCtx.bulletIndex);
      }
      setSelection(text.slice(0, 600));
      setSelectionCtx(ctx);
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, []);

  const clearSelection = () => {
    setSelection(null);
    setSelectionCtx(null);
    window.getSelection()?.removeAllRanges();
  };

  /* ─── finomítás — /conversion/webinar/callbook-funnel/refine ───── */

  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    const fullText = buildFullWebinarText(sections, sectionOrder, sectionMeta);
    if (!fullText) { toast.error("Nincs még kész szekció a finomításhoz."); return; }
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    const ctxToStore = selectionCtx;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post(EP.refine, {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        webinar_cim: inputs?.webinar_cim || "",
        mit_tanuljon: inputs?.mit_tanuljon || "",
        miert_te: inputs?.miert_te || "",
        hova_hivjuk: inputs?.hova_hivjuk || "",
        jelenlegi_poszt: fullText,
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
      });
      const data = res.data || {};
      setSuggestion({
        mod: data.mod === "snippet" ? "snippet" : "teljes",
        snippet: data.snippet || "",
        teljes: data.teljes_poszt || "",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
        ctx: ctxToStore || null,
      });
      setRefinePhase("suggestion");
    } catch (e) {
      setSuggestion({ mod: "error", instr: utasitas || (quickButtonId || ""), quote: quoteToSend || null, ctx: ctxToStore || null });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet = suggestion.mod === "snippet" ? suggestion.snippet : suggestion.teljes;
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post(EP.refine, {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        webinar_cim: inputs?.webinar_cim || "",
        mit_tanuljon: inputs?.mit_tanuljon || "",
        miert_te: inputs?.miert_te || "",
        hova_hivjuk: inputs?.hova_hivjuk || "",
        jelenlegi_poszt: buildFullWebinarText(sections, sectionOrder, sectionMeta),
        utasitas:
          suggestion.instr +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (previousSnippet ? ` Az előző javaslat ez volt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        quote: suggestion.quote || null,
      });
      const data = res.data || {};
      setSuggestion((s) => ({
        ...s,
        snippet: data.snippet || "",
        teljes: data.teljes_poszt || "",
        mod: data.mod === "snippet" ? "snippet" : "teljes",
      }));
    } catch (e) {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const snapshot = JSON.parse(JSON.stringify(sections));
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      // Ctx-aware csere: elsősorban a kijelölés eredeti context-je
      // (section_id / slide_number / field / bullet_index) alapján
      // pontos cserét végzünk. Ha az nem sikerül (pl. időközben
      // megváltozott a slide), akkor fuzzy fallback ladder.
      const { sections: nextSections, changed } = applySnippetReplacement(
        sections, suggestion.quote, suggestion.snippet, sectionOrder, suggestion.ctx || null
      );
      if (!changed) {
        toast.error("A kijelölt rész nem található már a szövegben.");
        return;
      }
      setUndoSnapshot(snapshot);
      setSections(nextSections);
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      // Teljes-módú válasz — nyers szöveg. Ilyenkor csak toast-oljuk,
      // hogy a scope túlmutat a slide-alapú struktúrán, és a felhasználó
      // kijelöléssel dolgozzon. (A backend snippet-módra van hangolva,
      // ha quote nélkül jön, a "teljes" markdown-t nem tudjuk struktúrába
      // visszatörni ide.)
      toast.info("A teljes átírás jelenleg csak kijelöléssel támogatott. Jelölj ki egy részt jobbra.");
      return;
    } else {
      return;
    }
    setSelection(null);
    setSelectionCtx(null);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setSaved(false);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle");
      setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setSections(undoSnapshot);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  /* ─── felső action pillek ──────────────────────────────────────── */

  const readyCount = sectionOrder.filter((sid) => sections[sid].status === "ready").length;
  const totalSlides = sectionOrder.reduce((acc, sid) => acc + (sections[sid].result?.slides?.length || 0), 0);
  const allDone = readyCount === sectionOrder.length;
  const hasAnyReady = readyCount > 0;

  const handleCopyAll = async () => {
    const text = buildFullWebinarText(sections, sectionOrder, sectionMeta);
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva.");
    } catch {
      toast.error("Másolás nem sikerült.");
    }
  };

  const handleDownload = () => {
    const text = buildFullWebinarText(sections, sectionOrder, sectionMeta);
    if (!text) return;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `webinar-${(inputs?.webinar_cim || "mex").toLowerCase().replace(/\s+/g, "-").slice(0, 40)}.txt`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    toast.success("Letöltve.");
  };

  const handleSave = async () => {
    if (savingVault) return;
    if (!allDone) {
      toast.info("A mentéshez várd meg, hogy minden szekció elkészüljön.");
      return;
    }
    setSavingVault(true);
    try {
      const carouselContent = buildCarouselContent(sections, inputs, sectionOrder, sectionMeta);
      const fullText = buildFullWebinarText(sections, sectionOrder, sectionMeta);
      await api.post(EP.save, {
        voice_id: activeAlap.voice || null,
        client_id: activeAlap.client || null,
        offer_id: activeAlap.offer || null,
        webinar_cim: inputs?.webinar_cim || "",
        mit_tanuljon: inputs?.mit_tanuljon || "",
        miert_te: inputs?.miert_te || "",
        hova_hivjuk: inputs?.hova_hivjuk || "",
        teljes_poszt_szoveg: fullText,
        carousel_content: carouselContent,
        post_title: inputs?.webinar_cim || "",
        post_first_line: "",
        agent_variant: agentVariant,
      });
      setSaved(true);
      toast.success("Mentve a Tudástárba.");
      // A Tudástárba mentés sikeres — a draft már NEM szükséges; a
      // szülő komponens kitörli a variant bucket-jét, hogy a következő
      // belépésnél tiszta lappal induljon a user.
      if (typeof onSavedToVault === "function") {
        try { onSavedToVault(); } catch { /* noop */ }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mentés sikertelen.";
      toast.error(msg);
    } finally {
      setSavingVault(false);
    }
  };

  /* ─── render ─────────────────────────────────────────────────── */

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  const inputDisabled = isLoading || showSuggestion;
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? suggestion.snippet : suggestion.teljes)
    : "";
  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : "Jelölj ki egy részt jobboldalt, és írd ide, mit változtassak.";

  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", color: "#e8e8f0", overflow: "hidden" }}
      data-testid="webinar-section-carousel"
    >
      <PostHeader
        onBackToOpening={onClose}
        activeTab="tab2"
        onTabChange={(t) => { if (t === "tab1") onClose?.(); }}
        tab2Disabled={false}
        headerTitle={
          agentVariant === "direct_sales"
            ? "Közvetlen értékesítés"
            : agentVariant === "landing_registration"
              ? "Webinár regisztrációs oldal"
              : "Webinárból telefonhívás"
        }
        tab2Label="Webinár"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">

        {/* ─── BAL — Finomító panel ────────────────────────────── */}
        <div
          className="flex flex-col border-r min-h-0 h-full"
          style={{ borderColor: "rgba(255,255,255,0.04)" }}
          data-testid="webinar-carousel-finomito"
        >
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div
                className="font-heading"
                style={{
                  fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                  letterSpacing: "-0.015em", color: "#ffffff",
                  marginBottom: 18,
                }}
              >
                {readOnly ? "Mentett webinár." : "Elkészült a webinar."}
              </div>
              <div style={{
                fontSize: 16, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0",
              }}>
                {readOnly ? (
                  <>
                    Ez a webinár a Tudástárból lett visszatöltve — <strong>csak
                    olvasásra</strong>. A slide-okat jobb oldalon szekcióról
                    szekcióra megnézheted, és a felső pillekkel másolhatod vagy
                    letöltheted a teljes tartalmat. Finomítás és újramentés a
                    következő körben érkezik.
                  </>
                ) : (
                  <>
                    Olvasd át a jobb oldalon a slide-okat, szekcióról szekcióra.
                    Ha valamit finomítanál, jelölj ki egy részt a slide-on, és írd
                    lent, mit változtassak. Ha minden rendben, mentsd el a Tudástárba.
                  </>
                )}
              </div>
            </div>

            {/* Selection workspace */}
            {!readOnly && (
            <>
            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="webinar-carousel-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{
                            fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                            color: "#8a8a9c", fontWeight: 600, marginBottom: 4,
                          }}>KIJELÖLT RÉSZ</div>
                          <div style={{
                            fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                            display: "-webkit-box", WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical", overflow: "hidden",
                          }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            aria-label="Kijelölés törlése"
                            style={{
                              width: 22, height: 22, borderRadius: 5,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              color: "#9898ad", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}
                            data-testid="webinar-carousel-selection-clear"
                          >
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !showSuggestion && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="webinar-carousel-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button
                          key={qa.id}
                          type="button"
                          onClick={() => requestRefine(qa.instruction, qa.id)}
                          data-testid={`webinar-carousel-quick-${qa.id}`}
                          style={{
                            fontSize: 13, padding: "10px 14px", borderRadius: 999,
                            background: "rgba(124,108,242,0.10)",
                            border: "1px solid rgba(124,108,242,0.28)",
                            color: "#cfc8ff", cursor: "pointer",
                            fontFamily: "inherit", fontWeight: 500,
                            whiteSpace: "nowrap", textAlign: "center", width: "100%",
                          }}
                        >{qa.label}</button>
                      ))}
                    </div>
                  )}

                  {isLoading && (
                    <div
                      className="flex items-center gap-2.5"
                      style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                      data-testid="webinar-carousel-refine-loading"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {showSuggestion && (
                    <div data-testid="webinar-carousel-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="webinar-carousel-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
            </>
            )}
          </div>

          {!readOnly && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) requestRefine();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="webinar-carousel-finomito-form"
          >
            <div style={{
              background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
              border: "1px solid rgba(243,238,228,0.14)",
              borderRadius: 12,
              padding: "5px 5px 5px 14px",
              display: "flex", alignItems: "center", gap: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
              opacity: inputDisabled ? 0.5 : 1,
              pointerEvents: inputDisabled ? "none" : "auto",
            }}>
              <button
                type="button"
                aria-label="Csatolás"
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: "#8a8a9c", cursor: "pointer",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) requestRefine();
                  }
                }}
                placeholder={placeholder}
                data-testid="webinar-carousel-chat-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{
                  background: "transparent", border: "none", color: "white",
                  fontSize: 14, padding: "8px 0", fontFamily: "inherit",
                }}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || inputDisabled}
                data-testid="webinar-carousel-chat-send"
                aria-label="Küldés"
                style={{
                  width: 36, height: 36,
                  background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                  color: "white", borderRadius: 9, border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                  opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                  boxShadow: chatInput.trim() && !inputDisabled
                    ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                    : "none",
                }}
              >
                {isLoading
                  ? <CircleNotch size={15} weight="bold" className="animate-spin" />
                  : <PaperPlaneTilt size={15} weight="fill" />}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Tipp: jelölj ki egy részt jobboldalt — a kérésed arra fog vonatkozni.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
          )}
        </div>

        {/* ─── JOBB — standard result-container: sötét külső pane +
             krém belső cream card (MagnetPostFacebook mintára). ─── */}
        <div
          className="overflow-y-auto h-full"
          style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}
        >
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="webinar-carousel-cream-card"
            className="mr-auto cream-selectable"
            style={{
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
            }}
          >
            {/* Felső action pillek */}
            <div
              className="flex justify-end items-center"
              style={{ gap: 6, marginBottom: 20, position: "relative", zIndex: 1 }}
              data-html2canvas-ignore="true"
            >
              <CreamActionPill
                onClick={handleCopyAll}
                icon={copied ? CheckCircle : CopyIcon}
                label={copied ? "Másolva" : "Másolás"}
                testid="webinar-carousel-copy-all"
                disabled={!hasAnyReady}
                active={copied}
              />
              <CreamActionPill
                onClick={handleDownload}
                icon={DownloadSimple}
                label="Letöltés"
                testid="webinar-carousel-download"
                disabled={!hasAnyReady}
              />
              {!readOnly && (
                <CreamActionPill
                  onClick={handleSave}
                  icon={saved ? CheckCircle : DownloadSimple}
                  label={savingVault ? "Mentés…" : saved ? "Mentve" : "Mentés a Tudástárba"}
                  testid="webinar-carousel-save"
                  disabled={!allDone || savingVault}
                  active={saved}
                />
              )}
            </div>

            {/* Meta-fejléc — Satoshi Bold főcím + sötétebb arany
                akcent-vonal a cím alatt, a vonal jobb oldalán ül a
                teljes slide-szám. */}
            <div style={{
              marginTop: 48, marginBottom: 40,
            }} data-testid="webinar-carousel-meta">
              <h1
                className="font-heading"
                style={{
                  fontSize: 26, fontWeight: 700, color: "#0a0a12",
                  letterSpacing: "-0.014em", lineHeight: 1.22,
                  margin: 0,
                }}
                data-testid="webinar-carousel-title"
              >
                {inputs?.webinar_cim || "Webinár"}
              </h1>
              <div
                className="flex items-center"
                style={{ marginTop: 24, gap: 12 }}
              >
                <div style={{
                  flex: 1, height: 1,
                  background: GOLD,
                }} />
                {totalSlides > 0 ? (
                  <span
                    data-testid="webinar-section-carousel-progress"
                    style={{
                      fontSize: 11, color: GOLD, fontWeight: 700,
                      letterSpacing: "0.14em",
                      flexShrink: 0,
                    }}
                  >
                    {totalSlides} SLIDE
                  </span>
                ) : null}
              </div>
            </div>

            {/* Szekció-akkordionok — Avatar-mintás minimális fejléc:
                a szekció NEVE a hangsúly, meta ("N slide") diszkrét.
                Nagy státusz-badge-ek eltávolítva. */}
            <div>
              {sectionOrder.map((sid, idx) => {
                const sec = sections[sid];
                const meta = sectionMeta[sid];
                const slides = sec.result?.slides || [];
                const isReady = sec.status === "ready";
                const isFailed = sec.status === "failed";
                const isGenerating = sec.status === "generating";
                return (
                  <div
                    key={sid}
                    data-testid={`webinar-section-${sid}`}
                    style={{
                      borderTop: idx === 0 ? "none" : "0.5px solid rgba(20,30,40,0.08)",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpand(sid)}
                      data-testid={`webinar-section-${sid}-header`}
                      disabled={sec.status === "waiting"}
                      style={{
                        width: "100%",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "22px 4px",
                        background: "transparent", border: "none",
                        color: "inherit",
                        cursor: sec.status === "waiting" ? "not-allowed" : "pointer",
                        fontFamily: "inherit", textAlign: "left",
                      }}
                    >
                      <div className="flex items-baseline" style={{ gap: 12, minWidth: 0, flex: 1 }}>
                        <span
                          className="font-heading"
                          style={{
                            fontSize: 16, color: GOLD, fontWeight: 700,
                            letterSpacing: "0.10em", flexShrink: 0,
                            textTransform: "uppercase",
                          }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span style={{ color: GOLD, opacity: 0.55, fontWeight: 700, flexShrink: 0 }}>·</span>
                        <span
                          className="font-heading"
                          style={{
                            fontSize: 16, fontWeight: 700, color: GOLD,
                            letterSpacing: "0.10em", lineHeight: 1.35,
                            textTransform: "uppercase",
                          }}
                        >
                          {meta.name}
                        </span>
                        {isReady && slides.length ? (
                          <>
                            <span style={{ color: GOLD, opacity: 0.45, fontWeight: 700, flexShrink: 0 }}>·</span>
                            <span style={{ fontSize: 12, color: "#8a8a9c", fontWeight: 500, flexShrink: 0 }}>
                              {slides.length} slide
                            </span>
                          </>
                        ) : null}
                        {isGenerating ? (
                          <>
                            <span style={{ color: GOLD, opacity: 0.45, fontWeight: 700, flexShrink: 0 }}>·</span>
                            <span
                              className="flex items-center"
                              style={{ gap: 6, fontSize: 12, color: GOLD, fontWeight: 500 }}
                            >
                              <CircleNotch size={12} weight="bold" className="animate-spin" />
                              Készül…
                            </span>
                          </>
                        ) : null}
                      </div>
                      <div className="flex items-center" style={{ gap: 10 }}>
                        {isFailed ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); retrySection(sid); }}
                            data-testid={`webinar-section-${sid}-retry`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 5,
                              fontSize: 12, padding: "6px 12px",
                              background: "transparent",
                              border: "1px solid rgba(210,50,50,0.30)",
                              borderRadius: 999, color: "#b03a3a", cursor: "pointer",
                              fontFamily: "inherit", fontWeight: 500,
                            }}
                          ><ArrowClockwise size={12} weight="bold" /> Újrapróbálás</button>
                        ) : null}
                        {(isReady || isFailed) ? (
                          sec.expanded
                            ? <CaretUp size={13} weight="bold" style={{ color: "#8a8a9c" }} />
                            : <CaretDown size={13} weight="bold" style={{ color: "#8a8a9c" }} />
                        ) : null}
                      </div>
                    </button>

                    {sec.expanded && isReady ? (
                      <div style={{ padding: "4px 2px 24px" }} data-testid={`webinar-section-${sid}-body`}>
                        {slides.map((s, i) => (
                          <SlideCard key={s.slide_number || i} slide={s} sectionIdx={i} sectionId={sid} />
                        ))}
                      </div>
                    ) : null}

                    {sec.expanded && isFailed ? (
                      <div style={{ padding: "0 2px 18px", color: "#b03a3a", fontSize: 13 }} data-testid={`webinar-section-${sid}-error`}>
                        {sec.error || "Ismeretlen hiba."}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {allDone ? (
              <div
                data-testid="webinar-carousel-all-done"
                style={{
                  marginTop: 28, fontSize: 11.5, color: "#8a8a9c",
                  fontWeight: 500, letterSpacing: "0.02em",
                }}
              >
                Teljes webinár elkészült.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
